// src/components/common/ModalStyles.tsx

import React from "react"
import { SxProps, Theme } from "@mui/material/styles"
import theme from "@core/theme"
import { GlassText } from "@core/utils/glassStyles"
import { uiColors } from "@shared/components/theme"
import CloseIcon from "@mui/icons-material/Close"

// 공통 모달/레이어 헤더 스타일
export const modalHeaderStyle: SxProps<Theme> = {
  background: uiColors.layerHeader.activeBackground,
  borderBottom: `1px solid ${uiColors.layerHeader.activeBorder}`,
  color: uiColors.layerHeader.activeText,
  padding: "12px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  minHeight: "48px",
}
// 공통 모달/레이어 타이틀 스타일 (TitleBar와 동일)
export const modalTitleStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: "500",
  fontFamily: "'Pretendard', 'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  color: "rgba(0, 0, 0, 0.8)",
  margin: 0,
  lineHeight: 1.4,
  letterSpacing: "-0.01em",
  userSelect: "none",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
}

// 공통 모달/레이어 닫기 버튼 스타일 (TitleBar와 유사한 스타일)
export const modalCloseButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: "20px",
  fontWeight: "normal",
  color: "rgba(0, 0, 0, 0.6)",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: "4px",
  lineHeight: 1,
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

// 공통 모달/레이어 컨테이너 스타일
export const modalContainerStyle: SxProps<Theme> = {
  borderRadius: "8px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  border: "1px solid #e0e0e0",
  overflow: "hidden",
  backgroundColor: "#fff",
}

// DialogTitle 전용 스타일 (TitleBar와 동일)
export const dialogTitleStyle: SxProps<Theme> = {
  background: uiColors.layerHeader.activeBackground,
  borderBottom: `1px solid ${uiColors.layerHeader.activeBorder}`,
  padding: "6px 16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "25px",
  flexShrink: 0,
  fontSize: "16px",
  fontWeight: "500",
  fontFamily: "'Pretendard', 'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  color: uiColors.layerHeader.activeText,
  margin: 0,
  lineHeight: 1.4,
  letterSpacing: "-0.01em",
  userSelect: "none",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  position: "relative",
}

// DialogTitle 내부 닫기 버튼 스타일
export const dialogCloseButtonStyle: SxProps<Theme> = {
  position: "absolute",
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(0, 0, 0, 0.6)",
  padding: "3px",
  width: "28px",
  height: "28px",
  minWidth: "28px",
  minHeight: "28px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    color: "rgba(0, 0, 0, 0.9)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}

// Dialog 전용 스타일 (기본 크기 800px)
export const dialogStyle: SxProps<Theme> = {
  "& .MuiDialog-paper": {
    width: "1280px",
    maxWidth: "1280px",
    minWidth: "1280px",
    borderRadius: "4px",
  },
}

// 공통 모달/레이어 바디 스타일
export const modalBodyStyle: React.CSSProperties = {
  padding: "16px",
  fontFamily: "'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSize: "14px",
  lineHeight: 1.5,
  color: "#333",
}

// 헤더 컴포넌트
interface ModalHeaderProps {
  title?: string
  icon?: React.ReactNode
  onClose: () => void
  headerActions?: React.ReactNode
  headerActionsRight?: React.ReactNode
  headerStyle?: React.CSSProperties
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ title, icon, onClose, headerActions, headerActionsRight, headerStyle }) => {
  return (
    <div
      className="layer-header"
      style={{
        background: uiColors.layerHeader.activeBackground,
        borderBottom: `1px solid ${uiColors.layerHeader.activeBorder}`,
        padding: "6px 16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "25px",
        flexShrink: 0,
        position: "relative",
        ...headerStyle,
      }}
    >
      {headerActions && (
        <div className="layer-header-actions" style={{ position: "absolute", left: "16px" }}>
          {headerActions}
        </div>
      )}
      {headerActionsRight && (
        <div className="layer-header-actions-right" style={{ position: "absolute", right: "44px" }}>
          {headerActionsRight}
        </div>
      )}
      {title && (
        <span
          className="layer-title"
          style={{
            fontSize: "16px",
            fontWeight: "500",
            fontFamily: "'Pretendard', 'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            color: uiColors.layerHeader.activeText,
            margin: 0,
            lineHeight: 1.4,
            letterSpacing: "-0.01em",
            userSelect: "none",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {title}
        </span>
      )}
      <button
        className="layer-close-button"
        onClick={onClose}
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          color: "rgba(0, 0, 0, 0.6)",
          cursor: "pointer",
          padding: "3px",
          width: "28px",
          height: "28px",
          minWidth: "28px",
          minHeight: "28px",
          borderRadius: "4px",
          lineHeight: 1,
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.08)"
          e.currentTarget.style.color = "rgba(0, 0, 0, 0.9)"
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = "transparent"
          e.currentTarget.style.color = "rgba(0, 0, 0, 0.6)"
        }}
      >
        <CloseIcon sx={{ fontSize: "20px" }} />
      </button>
    </div>
  )
}

